import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Col, Container, Row } from 'react-bootstrap';
import DonutCustomLegend from '../../components/ApexCharts/CustomLegends/DonutCustomLegend';
import DonutChart from '../../components/ApexCharts/DonutChart';
import Blockquote from '../../components/Blockquote';
import Button from '../../components/Button';
import GenericCard from '../../components/GenericCards';
import Link from '../../components/Link';
import useModal from '../../hooks/useModal';
import {
  addOrRemoveDaysToGivenDate,
  DateFormats,
  formatDate,
} from '../../utils/utils';
import AccumulatedWealthModal from '../widgets/totalRewardsModals/AccumulatedWealthModal';
import CompanyContributionModal from '../widgets/totalRewardsModals/CompanyContributionModal';

const dataCompanyContributions = {
  // The "grid padding - left" setting is a hack to reduce the huge margin to the left that donut renders.
  // See: https://github.com/apexcharts/apexcharts.js/issues/1272
  grid: {
    padding: {
      left: -20,
    },
  },
  legend: {
    show: false,
  },

  dataLabels: {
    enabled: false,
  },
  responsive: [
    {
      breakpoint: 480,
      options: {
        grid: {
          padding: {
            left: 0,
          },
        },
        legend: {
          show: false,
        },
        title: {},
      },
    },
  ],
  colors: ['#074CBC', '#F48462', '#07B4A0', '#EEA968', '#92E3A9'],
  series: [95072, 15905, 14791, 6500, 0],
  labels: [
    'Compensation',
    'Health and Welfare',
    'Retirement',
    'Equity',
    'Other Benefits',
  ],

  tooltip: {
    enabled: false,
  },

  // DISPLAY TEXT IN DONUT CENTRE:
  plotOptions: {
    pie: {
      donut: {
        labels: {
          show: true,
          name: {
            offsetY: 10,
          },
          total: {
            show: true,
            showAlways: true,
            fontSize: '18px',
            fontFamily: 'Roboto, Arial, sans-serif',
            fontWeight: 500,
            label: `$132,268`,
            formatter: function (a: any) {
              return '';
            },
          },
        },
      },
    },
  },
};

const dataAccumulatedWealth = {
  // The "grid padding - left" setting is a hack to reduce the huge margin to the left that the donut renders.
  // See: https://github.com/apexcharts/apexcharts.js/issues/1272
  grid: {
    padding: {
      left: -20,
    },
  },
  legend: {
    show: false,
  },

  dataLabels: {
    enabled: false,
  },
  responsive: [
    {
      breakpoint: 480,
      options: {
        grid: {
          padding: {
            left: 0,
          },
        },
        legend: {
          show: false,
        },
        title: {},
      },
    },
  ],
  colors: ['#074CBC', '#07B4A0', '#92E3A9'],
  series: [423692, 246244, 31212],
  labels: ['Compensation', 'Retirement', 'Other Benefits'],
  tooltip: {
    enabled: false,
  },

  // DISPLAY TEXT IN DONUT CENTRE:
  plotOptions: {
    pie: {
      donut: {
        labels: {
          show: true,
          name: {
            offsetY: 10,
          },
          total: {
            show: true,
            showAlways: true,
            fontSize: '18px',
            fontFamily: 'Roboto, Arial, sans-serif',
            fontWeight: 500,
            label: `$671,148`,
            formatter: function (a: any) {
              return '';
            },
          },
        },
      },
    },
  },
};

const TotalRewards = () => {
  const [
    showCompanyContribution,
    handleShowCompanyContribution,
    handleCloseCompanyContribution,
  ] = useModal();

  const [
    showAccumulatedWealthModal,
    handleShowAccumulatedWealth,
    handleCloseAccumulatedWealth,
  ] = useModal();

  return (
    <Container className="cnd-custom-container-large mb-12 px-lg-9 px-xl-0 mt-11">
      <Container className="px-7 px-lg-0 mb-11 mb-lg-12 mt-lg-13">
        <Row>
          <Col className="col-lg-5 mt-lg-12">
            <h2 className="text-sm-24-lg-32 mb-7">Total Rewards</h2>
          </Col>
        </Row>
        <Row className="px-lg-0 pt-10">
          <Col className="col-lg-8 col-12">
            <Row>
              <Col>
                <GenericCard variant={'white'}>
                  <Row>
                    <Col className="col-lg-10">
                      <Row>
                        <h5>Company's Contribution</h5>
                      </Row>
                      <Row>
                        <span className="text-light-grey text-fs-6 fw-light">
                          {`(${formatDate(
                            new Date(),
                            DateFormats.ONLY_YEAR
                          )} Annualized)`}
                        </span>
                      </Row>
                    </Col>
                    <Col className="text-end">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleShowCompanyContribution}
                      >
                        Details
                      </Button>
                      <CompanyContributionModal
                        showCompanyContributionModal={showCompanyContribution}
                        handleCloseCompanyContributionModal={
                          handleCloseCompanyContribution
                        }
                      />
                    </Col>
                  </Row>
                  <Row className="pt-12 px-lg-0 px-7">
                    <Col className="px-0 col-12 col-lg-5">
                      <DonutChart
                        series={dataCompanyContributions.series}
                        options={dataCompanyContributions}
                      />
                    </Col>
                    <Col className="pt-3 lh-xs pe-10">
                      <DonutCustomLegend
                        options={dataCompanyContributions}
                        hideBenefitText={true}
                      />
                    </Col>
                  </Row>
                </GenericCard>
              </Col>
            </Row>
            <Row className="pt-10">
              <Col>
                <GenericCard variant={'white'}>
                  <Row>
                    <Col className="col-lg-10">
                      <Row>
                        <h5>Accumulated Wealth</h5>
                      </Row>
                      <Row>
                        <span className="text-light-grey text-fs-6 fw-light">
                          {`(As of ${formatDate(
                            addOrRemoveDaysToGivenDate(new Date(), -1)
                          )})`}
                        </span>
                      </Row>
                    </Col>
                    <Col className="text-end">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleShowAccumulatedWealth}
                      >
                        Forecast
                      </Button>
                      <AccumulatedWealthModal
                        showAccumulatedWealthModal={showAccumulatedWealthModal}
                        handleCloseAccumulatedWealthModal={
                          handleCloseAccumulatedWealth
                        }
                      />
                    </Col>
                  </Row>
                  <Row className="pt-12 px-lg-0 px-7">
                    <Col className="px-0 col-12 col-lg-5">
                      <DonutChart
                        series={dataAccumulatedWealth.series}
                        options={dataAccumulatedWealth}
                      />
                    </Col>
                    <Col className="pt-3 lh-xs pe-10">
                      <DonutCustomLegend
                        options={dataAccumulatedWealth}
                        starredCategory="Other Benefits"
                        hideBenefitText={true}
                      />
                      <Row className="text-fs-6 fw-light lh-xs text-ligther-grey pt-7">
                        <Col>
                          * Your{' '}
                          <span className="fw-bolder">pension amount</span> is
                          an estimate of the amount payable as of your Normal
                          Retirement Age. Your accrued monthly amount is $1,212.
                          If you retire earlier than the stated retirement age
                          this amount will be less.
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </GenericCard>
              </Col>
            </Row>
          </Col>
          <Col className="col-lg-4 pt-lg-0 pt-10 px-10">
            <Row>
              <GenericCard variant={'white'}>
                <Row>
                  <Col className="title-span px-lg-0">
                    Let's help you understand this information
                  </Col>
                </Row>
                <Row className="border-bottom pt-9 ">
                  <Col className="fw-light text-fs-6 lh-xs px-lg-0 pb-3">
                    Company's Contribution
                  </Col>
                </Row>
                <Row className="lh-xs">
                  <Col className="fw-light text-fs-6 text-ligther-grey pt-5 px-lg-0">
                    Represents all of the wealth and compensation that the
                    company provides you. Use this tool to understand where that
                    value comes from and how to take advantage of it.
                  </Col>
                </Row>

                <Row className="lh-xs border-bottom pt-9">
                  <Col className="fw-light text-fs-6 px-lg-0 pb-3">
                    Accumulated Wealth
                  </Col>
                </Row>
                <Row className="lh-xs">
                  <Col className="fw-light text-fs-6 text-ligther-grey pt-5 px-lg-0">
                    <p>
                      Is today's starting point for your future financial
                      security when you retire.{' '}
                    </p>
                    <p>
                      Use the information on these pages to help you understand
                      where you are now, and to lead you to where you need to be
                      in the future.{' '}
                    </p>
                    <p>
                      Your Accumulated Wealth comes from numerous sources and
                      may not be completely up to date.
                    </p>
                  </Col>
                  <Col className="px-0 col-12">
                    <Row className="pt-5 mx-0 d-flex justify-content-end">
                      <Col className="d-flex justify-content-end px-0 col-lg-11">
                        <Link
                          className={'text-decoration-none'}
                          href={`${
                            process.env.PUBLIC_URL
                          }/${'total-rewards-statement-demo-example_v1.pdf'}`}
                          target={'_blank'}
                        >
                          <Button
                            className="wealth-compensation-btn w-100 mx-5 mx-lg-0 px-5"
                            variant="primary"
                            size="sm"
                          >
                            Wealth & Compensation Summary
                          </Button>
                        </Link>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </GenericCard>
            </Row>
            <Row className="pt-10 lh-xs">
              <Col className="default-card transparent">
                <Row>
                  <Col>Benefits Information</Col>
                </Row>
                <Row className="pt-12">
                  <Col>
                    <Link
                      rightIcon={light('arrow-up-right-from-square')}
                      isAlignAtEnd
                      isBlue
                      className="pt-3 pb-4 border-bottom"
                      href={`${process.env.PUBLIC_URL}/${'dc-guide-demo.pdf'}`}
                      target="_blank"
                    >
                      Read more information about your benefits
                    </Link>
                    <Link
                      rightIcon={light('arrow-up-right-from-square')}
                      isAlignAtEnd
                      isBlue
                      className="pt-4"
                      href={`${
                        process.env.PUBLIC_URL
                      }/${'cdt-savings-plan-spd-demo.pdf'}`}
                      target="_blank"
                    >
                      View methods and assumptions used in the calculations
                    </Link>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row className="pt-10 lh-xs">
              <Blockquote>
                <span>
                  {`The ${formatDate(
                    new Date(),
                    DateFormats.ONLY_YEAR
                  )} Equity contribution is equivalent to your annual
                  Dividend Distribution from any Stock Options/Grants. Your
                  Current Equity Balance under Accumulated Wealth totals the
                  value of your unvested and exercisable units, and factors in
                  the stock price and grant price if applicable.`}
                </span>
              </Blockquote>
            </Row>
          </Col>
        </Row>
      </Container>
    </Container>
  );
};
export default TotalRewards;
